import type { QueryClient } from "@tanstack/react-query";
import { flush, onSyncMessage, refreshCounts, type FlushResult } from "./outbox";
import { keys } from "./queries";

let timer: ReturnType<typeof setTimeout> | null = null;
let client: QueryClient | null = null;

function invalidate(domains: Iterable<string>) {
  if (!client) return;
  for (const d of domains) {
    if (d === "ascents") {
      void client.invalidateQueries({ queryKey: keys.ascents });
      // A climbed peak leaves the Bucket list on the server too.
      void client.invalidateQueries({ queryKey: keys.lists });
      void client.invalidateQueries({ queryKey: ["peak"] });
    }
    if (d === "lists") {
      void client.invalidateQueries({ queryKey: keys.lists });
      void client.invalidateQueries({ queryKey: ["peak"] });
    }
    if (d === "peaks") void client.invalidateQueries({ queryKey: keys.extraPeaks });
    if (d === "pins") void client.invalidateQueries({ queryKey: keys.pins });
    if (d === "profile") void client.invalidateQueries({ queryKey: keys.profile });
    if (d === "social") invalidateSocial(client);
    // An invitation accepted: a new ascent of one's own (and its peaks out of the Bucket list), and the social side.
    if (d === "groups") {
      void client.invalidateQueries({ queryKey: keys.ascents });
      void client.invalidateQueries({ queryKey: keys.lists });
      void client.invalidateQueries({ queryKey: ["peak"] });
      invalidateSocial(client);
    }
  }
}

/** Every social read: only the mounted ones refetch, the rest are marked stale. */
function invalidateSocial(qc: QueryClient) {
  for (const queryKey of [["social"], ["person"], ["follows"], ["notices"], ["feed"], ["comments"], ["boards"], ["challenges"]]) void qc.invalidateQueries({ queryKey });
}

export async function syncNow(opts: { refetch?: boolean } = {}): Promise<FlushResult | null> {
  const res = await flush();
  if (res?.domains.size) invalidate(res.domains);
  if (opts.refetch && !res?.stoppedBy && client) {
    void client.invalidateQueries({ queryKey: keys.ascents });
    void client.invalidateQueries({ queryKey: keys.lists });
    void client.invalidateQueries({ queryKey: keys.extraPeaks });
    void client.invalidateQueries({ queryKey: keys.pins });
    void client.invalidateQueries({ queryKey: keys.profile });
    void client.invalidateQueries({ queryKey: keys.config });
    invalidateSocial(client);
  }
  return res;
}

export function scheduleFlush(delay = 300) {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    void syncNow();
  }, delay);
  // Browsers with Background Sync (not iOS) can also retry from the service worker later.
  void navigator.serviceWorker?.ready
    .then((r) => (r as ServiceWorkerRegistration & { sync?: { register(tag: string): Promise<void> } }).sync?.register("outbox"))
    .catch(() => undefined);
}

/**
 * iOS Safari has no Background Sync: flush on launch, when the app becomes visible again,
 * when the network comes back, periodically while visible with pending work, and on demand.
 */
export function installSyncTriggers(qc: QueryClient) {
  client = qc;
  void refreshCounts();
  void syncNow({ refetch: true });

  const onVisible = () => {
    if (document.visibilityState === "visible") void syncNow({ refetch: true });
  };
  const onOnline = () => void syncNow({ refetch: true });
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("online", onOnline);
  window.addEventListener("pageshow", onVisible);
  const interval = setInterval(() => {
    if (document.visibilityState === "visible" && navigator.onLine) void syncNow();
  }, 60_000);
  const off = onSyncMessage((m) => {
    if (m.type === "flushed") invalidate(m.domains);
    void refreshCounts();
  });
  return () => {
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("pageshow", onVisible);
    clearInterval(interval);
    off();
  };
}
